import {
    BenefitTypeDisplayTextMap,
    BenefitTypes,
    CONTRACT_RATE,
    RouteCodeDisplayTextMap,
} from '@strata/constants';
import { conditionalString } from '@strata/helper-functions';
import { FormSection } from '@strata/react-form-section';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { ServicingRate } from '@strata/react-servicing-rate';
import Decimal from 'decimal.js';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';

import { RouteConditionValue } from 'pages/routeManagement/RouteConditionValue';
import { getInferredSupplierDisplayName, SendToOptions } from 'services/RouteRuleHelpers';

const getConditionsPreview = (conditions, conditionOptionsMap) => {
    if (conditions.length === 0) {
        return (
            <DetailLabelValuePair
                className="margin__bottom--medium"
                label="And Have Conditions That Match"
            />
        );
    }

    return conditions.map((condition, i) => (
        <DetailLabelValuePair
            key={condition.condition_type}
            label={i === 0 ? 'And Have Conditions That Match' : 'And'}
            value={
                <RouteConditionValue
                    condition={condition}
                    conditionOptionsMap={conditionOptionsMap}
                />
            }
        />
    ));
};

const getRateOverridePreview = (values) => {
    if (!(values.referent_price_type || values.use_deepest_rate)) {
        return null;
    }

    if (!values.referent_price_type && values.use_deepest_rate) {
        return <DetailLabelValuePair label="At" value="The lesser of rates" />;
    }

    const rateOverride = (() => {
        if (values.referent_price_type === 'Contract') {
            return CONTRACT_RATE;
        }
        return values.rate_override != null
            ? Decimal.div(values.rate_override, 100).toNumber()
            : undefined;
    })();
    return (
        <DetailLabelValuePair
            label="At"
            value={
                <div style={{ display: 'flex' }}>
                    <ServicingRate
                        servicingRate={rateOverride}
                        referentPriceType={values.referent_price_type}
                    />
                    {values.use_deepest_rate && <span>, or the lesser of rates</span>}
                </div>
            }
        />
    );
};

export const RoutePreviewSection = ({
    benefit = undefined,
    isLoading,
    payerSegmentOptions = undefined,
    conditionOptionsMap,
    supplierOptions = undefined,
    networkOptions = undefined,
}) => {
    const formik = useFormikContext();
    return (
        <FormSection
            sectionTitle="Preview"
            isLoading={isLoading}
            content={
                <div className="flex__gap--medium" style={{ flexDirection: 'column' }}>
                    <div className="sds_route-preview__segments">
                        <div className="sds_route-preview__when font-semi-bold">
                            {`When ${conditionalString(benefit, `${BenefitTypeDisplayTextMap[benefit]} `)}referrals from:`}
                        </div>
                        {formik.values.payer_segments.map((segment) => {
                            const segmentName = payerSegmentOptions?.find(
                                (option) => option.value === segment.segment_id,
                            )?.name;

                            return (
                                <div
                                    key={segment.distinct_id}
                                    className="sds_route-preview__segment"
                                >
                                    {[
                                        segment.segment_id,
                                        conditionalString(segmentName, `(${segmentName})`),
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                </div>
                            );
                        })}
                    </div>
                    <DetailLabelValuePair
                        label="Hit The Route Rule"
                        value={formik.values.route_name}
                    />
                    {getConditionsPreview(formik.values.conditions, conditionOptionsMap)}
                    <DetailLabelValuePair
                        label="They Will Route By"
                        value={RouteCodeDisplayTextMap[formik.values.route_code]}
                    />
                    <DetailLabelValuePair
                        label="To"
                        value={(() => {
                            if (formik.values.supplier_npi) {
                                return supplierOptions?.find(
                                    (supplier) =>
                                        supplier.supplier_npi === formik.values.supplier_npi,
                                )?.name;
                            }
                            if (formik.values.network_list_id) {
                                return networkOptions?.find(
                                    (network) => network.value === formik.values.network_list_id,
                                )?.name;
                            }
                            if (formik.values.send_to === SendToOptions.INFERRED_SUPPLIER) {
                                return getInferredSupplierDisplayName(formik.values);
                            }
                            return undefined;
                        })()}
                    />
                    {getRateOverridePreview(formik.values)}
                </div>
            }
        />
    );
};

RoutePreviewSection.propTypes = {
    benefit: PropTypes.oneOf(Object.values(BenefitTypes)),
    isLoading: PropTypes.bool.isRequired,
    payerSegmentOptions: PropTypes.arrayOf(
        PropTypes.shape({ name: PropTypes.string, value: PropTypes.string }),
    ),
    conditionOptionsMap: PropTypes.shape({}).isRequired,
    supplierOptions: PropTypes.arrayOf(
        PropTypes.shape({ supplier_npi: PropTypes.string.isRequired }),
    ),
    networkOptions: PropTypes.arrayOf(
        PropTypes.shape({ name: PropTypes.string.isRequired, value: PropTypes.string.isRequired }),
    ),
};
